// Leaderboard (ui-qol §2.1): agar's top-right box — black 40 %, square corners, 156u wide,
// "Leaderboard" in 18u Ubuntu, rows "{rank}. {name}" in 11u at a 14u pitch, your row in
// #FFAAAA, and an 11th row "{rank}. {your nick}" when you are outside the top 10 (no separator,
// no party star, no bot tag; empty names read "Unnamed cell"). Updated from the LEADERBOARD feed
// (1 Hz).
//
// Owner exception (a): each row also shows that player's live points, right-aligned — the score
// in free rooms, the value in money rooms (USD, or SOL with the label setting) — and the extra
// row shows your own rank and points.
//
// Consumer: app.ts (mounts `el` into #hud; calls `update` when store.leaderboard.version or the
// label unit changed). `leaderboardRows` is the DOM-free view model (unit-tested); the DOM part
// keeps 11 row elements and only rewrites what changed.

import { type LeaderboardSection, MAX_LEADERBOARD_ENTRIES } from '@ballio/shared';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';

export interface LeaderboardHud {
  readonly el: HTMLElement;
  update(lb: LeaderboardSection, opts?: LeaderboardOptions): void;
  setVisible(visible: boolean): void;
}

/** One rendered row. */
export interface LeaderboardRow {
  /** 1-based rank. */
  readonly rank: number;
  /** Display name: the entry's name ("Unnamed cell" when empty); your nick on the extra row. */
  readonly name: string;
  /** The row's points, formatted (owner exception (a)); null without the amounts trailer. */
  readonly amount: string | null;
  /** Your row (#FFAAAA). */
  readonly isYou: boolean;
  /** The 11th "{rank}. {your nick}" row shown when your rank is beyond the list. */
  readonly extra: boolean;
}

export interface LeaderboardOptions {
  /** Your nick for the extra row (the current life's); null/"" reads "Unnamed cell". */
  readonly ownNick?: string | null;
  /**
   * Formats an amount of the board's `amountKind` (score or lamports); omit to show none.
   * app.ts passes `formatLeaderboardAmount` with the label unit and price.
   */
  readonly formatAmount?: ((amount: number) => string) | null;
}

/** The name a leaderboard shows for `name` (ui-qol §2.1: empty → "Unnamed cell"). */
export function displayName(name: string | null | undefined): string {
  return name == null || name.trim() === '' ? t('hud.unnamed') : name;
}

/**
 * The rows for a LEADERBOARD section: at most `max` entries in order, then, when `yourRank` is
 * beyond the entries shown, one extra "{rank}. {your nick}" row with your own amount.
 */
export function leaderboardRows(
  lb: LeaderboardSection,
  opts: LeaderboardOptions = {},
  max: number = MAX_LEADERBOARD_ENTRIES,
): LeaderboardRow[] {
  const fmt = lb.amountKind !== undefined ? (opts.formatAmount ?? null) : null;
  const amountOf = (v: number | undefined): string | null =>
    fmt !== null && v !== undefined && Number.isFinite(v) ? fmt(v) : null;
  const rows: LeaderboardRow[] = [];
  const shown = lb.entries.slice(0, max);
  shown.forEach((e, i) => {
    rows.push({
      rank: i + 1,
      name: displayName(e.name),
      amount: amountOf(e.amount),
      isYou: e.isYou,
      extra: false,
    });
  });
  const listedYou = shown.some((e) => e.isYou);
  if (lb.yourRank > shown.length && !listedYou) {
    rows.push({
      rank: lb.yourRank,
      name: displayName(opts.ownNick),
      amount: amountOf(lb.yourAmount),
      isYou: true,
      extra: true,
    });
  }
  return rows;
}

/** "1. Name" (ui-qol §2.1). */
export function rowLabel(row: Pick<LeaderboardRow, 'rank' | 'name'>): string {
  return `${row.rank}. ${row.name}`;
}

/** Box height in HUD units for `rows` rows (ui-qol §2.1: (42 + 14·n)u). */
export function leaderboardHeightU(rows: number): number {
  return 42 + 14 * Math.max(0, rows);
}

interface RowEls {
  readonly li: HTMLLIElement;
  readonly label: HTMLSpanElement;
  readonly amount: HTMLSpanElement;
  key: string;
}

export function createLeaderboard(): LeaderboardHud {
  const slots = MAX_LEADERBOARD_ENTRIES + 1;
  const rowEls: RowEls[] = [];
  const list = h('ol', { class: 'lb-list' });
  for (let i = 0; i < slots; i++) {
    const label = h('span', { class: 'lb-label' });
    const amount = h('span', { class: 'lb-amount' });
    const li = h('li', { class: 'lb-row', hidden: true }, label, amount);
    list.appendChild(li);
    rowEls.push({ li, label, amount, key: '' });
  }
  const el = h(
    'section',
    { class: 'hud-leaderboard', 'aria-label': t('hud.leaderboard'), hidden: true },
    h('h2', { class: 'lb-title' }, t('hud.leaderboard')),
    list,
  );
  let shownRows = -1;

  return {
    el,
    update(lb: LeaderboardSection, opts: LeaderboardOptions = {}): void {
      const rows = leaderboardRows(lb, opts);
      if (rows.length !== shownRows) {
        shownRows = rows.length;
        el.style.setProperty('--lb-rows', String(rows.length));
      }
      for (let i = 0; i < rowEls.length; i++) {
        const slot = rowEls[i] as RowEls;
        const row = rows[i];
        if (!row) {
          if (slot.key !== '') {
            slot.key = '';
            slot.li.hidden = true;
          }
          continue;
        }
        const key = `${row.rank}\u0000${row.name}\u0000${row.amount ?? ''}\u0000${+row.isYou}`;
        if (key === slot.key) continue;
        slot.key = key;
        slot.li.hidden = false;
        slot.label.textContent = rowLabel(row);
        slot.amount.textContent = row.amount ?? '';
        slot.amount.hidden = row.amount === null;
        slot.li.classList.toggle('is-you', row.isYou);
      }
    },
    setVisible(visible: boolean): void {
      show(el, visible);
    },
  };
}
